import { Box, Flex, Link, SimpleGrid } from "@chakra-ui/react";
import { useQuery } from "@apollo/client";
import { Link as RouterLink } from "react-router-dom";
import { MODULES, MODULE_COUNT_FALLBACK } from "./homeContent";
import { HOME_AVAILABLE_MODULES, HomeAvailableModulesData } from "./queries";
import { Body, H2, H3, Inner, Kicker, Lead, Mono, Section, useHomeTokens } from "./primitives";

/**
 * #modules — "Switch on only what you need".
 *
 * The six cards are the design's static showcase. The count in the "SEE ALL"
 * link is LIVE from `availableModules` (public, no login) and falls back to the
 * design's 81 only while the query is loading or if it errors.
 */
export const Modules = () => {
  const t = useHomeTokens();
  const { data } = useQuery<HomeAvailableModulesData>(HOME_AVAILABLE_MODULES, {
    fetchPolicy: "cache-first",
    errorPolicy: "all",
  });
  const liveCount = data?.availableModules?.length;
  const count = liveCount && liveCount > 0 ? liveCount : MODULE_COUNT_FALLBACK;

  return (
    <Section id="modules" alt>
      <Inner>
        <Flex wrap="wrap" align="flex-end" justify="space-between" gap="24px" mb="52px">
          <Flex direction="column" gap="18px" maxW="640px">
            <Kicker>Business module suite</Kicker>
            <H2>Switch on only what you need</H2>
            <Lead>
              Every module runs standalone or combines into one integrated platform. All of them are blockchain and AI
              compatible.
            </Lead>
          </Flex>
          <Link
            as={RouterLink}
            to="/modules"
            color={t.textFaint}
            title={`Opens the full module directory — all ${count} registered modules with what each one does. Reading only.`}
            _hover={{ textDecoration: "none", color: t.secondary }}
          >
            <Mono fontSize="11.5px" color="inherit" letterSpacing=".1em">
              {count} MODULES REGISTERED · SEE ALL →
            </Mono>
          </Link>
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap="16px">
          {MODULES.map((m) => (
            <Box
              key={m.title}
              p="26px"
              borderRadius="16px"
              borderWidth="1px"
              borderColor={t.cardBorder}
              bg={t.cardTint}
              display="flex"
              flexDirection="column"
              gap="13px"
              minW={0}
              transition="border-color .2s ease, background .2s ease"
              _hover={{ bg: t.cardTintHover, borderColor: t.primaryBorder }}
            >
              <Flex align="center" gap="11px">
                <Box as="span" fontSize="18px" lineHeight="1" aria-hidden="true">
                  {m.icon}
                </Box>
                <H3 fontSize="1.08rem">{m.title}</H3>
              </Flex>
              <Body fontSize=".92rem">{m.body}</Body>
              <Box
                as="ul"
                listStyleType="none"
                m={0}
                p={0}
                display="grid"
                gap="7px"
                mt="4px"
                pt="14px"
                borderTop="1px solid"
                borderColor={t.hairline}
              >
                {m.points.map((p) => (
                  <Box as="li" key={p} fontSize=".86rem" color={t.textMuted}>
                    {p}
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </SimpleGrid>
      </Inner>
    </Section>
  );
};
